import React from 'react';
import {
    AttributeDefinitionFieldType,
    AttributeDefinitionSizeKeyType,
    AttributeDefinitionState,
    AttributeValueState,
} from '../../../types/zitoneoServicesTypes';
import SwitchInput from '../../../../../muiComponents/switchInput/SwitchInput';
import TextInput from '../../../../../muiComponents/TextInput/TextInput';
import DateInput from '../../../../../muiComponents/DateInput';
import { getRandomNumber } from '../../../../../utils/usefulFunctions';
import useTranslate from '../../../../../customHooks/useTranslate';
import MultipleValueInput from './MultipleValueInput';

interface InputFieldFactoryProps {
    attributeDefinition: AttributeDefinitionState;
    values: AttributeValueState[];
    disabled: boolean;
    onChange: (
        id: string,
        value: string,
        idService: string,
        idAttributeDefinition: number
    ) => void;
    onDelete: (id: string, idService: string) => void;
    idService: string;
}

interface FieldControlProps {
    fieldType: AttributeDefinitionFieldType;
    label: string;
    required: boolean;
    disabled: boolean;
    onChange: (value: string) => void;
    value?: string;
    size: AttributeDefinitionSizeKeyType;
    characterLimit: number;
}

const FieldControl = ({
    fieldType,
    label,
    required,
    disabled,
    onChange,
    value = '',
    size,
    characterLimit,
}: FieldControlProps) => {
    const translate = useTranslate();
    switch (fieldType) {
        case AttributeDefinitionFieldType.BOOLEAN:
            return (
                <div className='switch-input__container'>
                    <SwitchInput
                        labelText={label}
                        checked={value === 'True' ? true : false}
                        disabled={disabled}
                        onChangeMethod={(_e: unknown, checked: boolean) => {
                            onChange(checked ? 'True' : 'False');
                        }}
                        labelPlacement='start'
                        sx={{
                            width: '100%',
                            justifyContent: 'space-between',
                            margin: '2px',
                        }}
                    />
                </div>
            );
        case AttributeDefinitionFieldType.NUMBER:
            return (
                <TextInput
                    labelText={label}
                    required={required}
                    disabled={disabled}
                    type='number'
                    deleteIcon={false}
                    value={value}
                    sx={{
                        backgroundColor: 'var(--background)',
                        paddingBlock: '4px',
                    }}
                    labelProps={{
                        sx: {
                            backgroundColor: 'var(--background)',
                            padding: '4px',
                        },
                    }}
                    handleChange={(_e: unknown, newValue: string) => {
                        onChange(newValue);
                    }}
                />
            );
        case AttributeDefinitionFieldType.DATE:
            return (
                <DateInput
                    labelText={label}
                    disabled={disabled}
                    onChange={(newDate: Date | null) => {
                        onChange(newDate ? newDate.toISOString() : '');
                    }}
                    value={value}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'var(--background)',
                            paddingBlock: '4px',
                        },
                        '& .MuiInputLabel-root': {
                            backgroundColor: 'var(--background) !important',
                            padding: '4px',
                        },
                    }}
                />
            );
        case AttributeDefinitionFieldType.STRING:
        default:
            return (
                <TextInput
                    labelText={label}
                    required={required}
                    disabled={disabled}
                    deleteIcon={false}
                    multiline={size === AttributeDefinitionSizeKeyType.LARGE}
                    rows={size === AttributeDefinitionSizeKeyType.LARGE ? 3 : 1}
                    sx={{
                        backgroundColor: 'var(--background)',
                        paddingBlock: '4px',
                    }}
                    labelProps={{
                        sx: {
                            backgroundColor: 'var(--background) !important',
                            padding: '4px',
                        },
                    }}
                    handleChange={(_e: unknown, newValue: string) => {
                        onChange(newValue.slice(0, characterLimit));
                    }}
                    value={value}
                    error={value.length >= characterLimit}
                    errorMessage={
                        value.length >= characterLimit
                            ? `${translate(
                                  'maximum_character_limit_reached'
                              )}. ${characterLimit} ${translate('characters')}.`
                            : undefined
                    }
                    helperText={
                        value.length < characterLimit
                            ? `${value.length} / ${characterLimit} ${translate(
                                  'characters'
                              )}.`
                            : undefined
                    }
                />
            );
    }
};

const InputFieldFactory = ({
    attributeDefinition,
    disabled,
    onChange,
    onDelete,
    values,
    idService,
}: InputFieldFactoryProps) => {
    if (attributeDefinition.Unique) {
        const currentValue = values[0]?.Value || '';

        return (
            <FieldControl
                fieldType={attributeDefinition.FieldType}
                label={attributeDefinition.Name}
                required={attributeDefinition.Required}
                disabled={disabled}
                value={currentValue}
                onChange={value =>
                    onChange(
                        values[0]?.Id ?? `-${getRandomNumber()}`,
                        value,
                        idService,
                        attributeDefinition.IdAttributeDefinition
                    )
                }
                size={attributeDefinition.SizeKey}
                characterLimit={attributeDefinition.CharacterLimit}
            />
        );
    } else {
        return (
            <MultipleValueInput
                definition={attributeDefinition}
                values={values}
                disabled={disabled}
                onAddValue={value =>
                    onChange(
                        `-${getRandomNumber()}`,
                        value,
                        idService,
                        attributeDefinition.IdAttributeDefinition
                    )
                }
                onRemoveValue={valueId => onDelete(valueId, idService)}
                onEditValue={(valueId, newValue) =>
                    onChange(
                        valueId,
                        newValue,
                        idService,
                        attributeDefinition.IdAttributeDefinition
                    )
                }
                characterLimit={attributeDefinition.CharacterLimit}
            />
        );
    }
};

export default InputFieldFactory;
